/**
 * Fields needed to derive the composite key under which a product's data
 * (name, rules, versions) is stored in `state.skus`.
 */
export type SkuKeyParts = {
    sku: string;
    skuVersion?: number;
    merchantSku?: string;
    merchantVersion?: number;
};

/** The SKU and version under which a product's data is requested from the services. */
export type EffectiveSku = {
    effectiveSku: string;
    effectiveVersion?: number;
};

/**
 * Resolves the effective SKU and version for a product.
 *
 * The version is coupled to the selected SKU. When a merchant (PRD) SKU is present, its
 * merchant version is used, or none when the merchant version is absent.
 */
export function resolveEffectiveSku({ sku, skuVersion, merchantSku, merchantVersion }: SkuKeyParts): EffectiveSku {
    return merchantSku
        ? { effectiveSku: merchantSku, effectiveVersion: merchantVersion }
        : { effectiveSku: sku, effectiveVersion: skuVersion };
}

/**
 * Derives the canonical `state.skus` cache key for a product from its effective SKU and
 * version. The version segment is omitted when no effective version is resolved.
 */
export function deriveSkuKey(parts: SkuKeyParts): string {
    const { effectiveSku, effectiveVersion } = resolveEffectiveSku(parts);
    return effectiveVersion ? `${effectiveSku}-${effectiveVersion}` : effectiveSku;
}
